import React, { FC, useState, useMemo } from 'react';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import './HowOftenTrainStep.scss';
import { ReactComponent as MorningIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/morning.svg';
import { ReactComponent as EveningIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/evening.svg';
import { ReactComponent as LunchIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/lunch.svg';

interface HowOftenTrainStepProps {
  clientName?: string;
  value?: string[];
  reminderValue?: string;
  onChange?: (days: string[], reminder: string) => void;
}

const iconMapping: Record<string, React.ReactNode> = {
  morning: <MorningIcon />,
  lunch: <LunchIcon />,
  evening: <EveningIcon />,
};

// How often will they train?
export const HowOftenTrainStep: FC<HowOftenTrainStepProps> = ({
  value,
  reminderValue,
  onChange,
}) => {
  const [localDays, setLocalDays] = useState<string[]>([]);
  const [localReminder, setLocalReminder] = useState<string>('');

  const selectedDays = value !== undefined ? value : localDays;
  const selectedReminder = reminderValue !== undefined ? reminderValue : localReminder;

  const { data: programMasterData, isLoading, isError } = useGetProgramMasterData();
  const trainingSchedule = programMasterData?.trainingSchedule;

  const trainingDaysField = trainingSchedule?.fields?.find((f: any) => f.id === 'training_days');
  const preferredTimeField = trainingSchedule?.fields?.find((f: any) => f.id === 'preferred_time');

  const trainingDaysOptionsData = trainingDaysField?.options;
  const trainingDaysOptions = useMemo(() => {
    return [...(trainingDaysOptionsData || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [trainingDaysOptionsData]);

  const preferredTimeOptionsData = preferredTimeField?.options;
  const preferredTimeOptions = useMemo(() => {
    return [...(preferredTimeOptionsData || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [preferredTimeOptionsData]);

  const handleDaySelect = (id: string) => {
    const newDays = selectedDays.includes(id)
      ? selectedDays.filter((i) => i !== id)
      : [...selectedDays, id];
    if (onChange) onChange(newDays, selectedReminder);
    setLocalDays(newDays);
  };

  const handleReminderSelect = (reminderId: string) => {
    const newReminder = reminderId === selectedReminder ? '' : reminderId;
    if (onChange) onChange(selectedDays, newReminder);
    setLocalReminder(newReminder);
  };

  if (isLoading) {
    return <div className="how-often-train-step">{'Loading options...'}</div>;
  }

  if (isError || !trainingSchedule) {
    return <div className="how-often-train-step">{'Error loading options.'}</div>;
  }

  return (
    <div className="how-often-train-step">
      <h2 className="step-title">{trainingSchedule.title}</h2>
      <p className="step-subtitle">{trainingSchedule.subtitle}</p>

      <div className="days-container">
        {trainingDaysOptions.map((day) => (
          <div
            key={day.id}
            className={`day-circle ${selectedDays.includes(day.slug) ? 'selected' : ''}`}
            onClick={() => handleDaySelect(day.slug)}
          >
            {day.shortTitle || day.title?.[0]}
          </div>
        ))}
      </div>

      <div className="days-text">
        {selectedDays.length > 0
          ? `${selectedDays.length} DAY${selectedDays.length > 1 ? 'S' : ''} PER WEEK`
          : '\u00A0'}
      </div>

      {selectedDays.length > 0 && (
        <div className="reminder-section fadeIn">
          <h3 className="reminder-title">{preferredTimeField?.title}</h3>
          <div className="reminder-options">
            {preferredTimeOptions.map((timeOpt) => (
              <button
                key={timeOpt.id}
                className={`reminder-btn ${selectedReminder === timeOpt.slug ? 'selected' : ''}`}
                onClick={() => handleReminderSelect(timeOpt.slug)}
              >
                <div className="icon">{iconMapping[timeOpt.id] || null}</div>
                {timeOpt.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
